import { Authorization, Token, UserInfo } from '@/constants/authorization';
import { getSearchValue } from './common-util';
import { getOidcAccessTokenSync, removeOidcData } from './oidc-manager';
export { Authorization };
const KeySet = [Authorization, Token, UserInfo];
// The page to return to after signing in (redirectToLogin / takeLoginReturnTo).
const LoginReturnToKey = 'loginReturnTo';

const storage = {
  getAuthorization: () => {
    return localStorage.getItem(Authorization);
  },
  getToken: () => {
    return localStorage.getItem(Token);
  },
  getUserInfo: () => {
    return localStorage.getItem(UserInfo);
  },
  getUserInfoObject: () => {
    return JSON.parse(localStorage.getItem('userInfo') || '');
  },
  setAuthorization: (value: string) => {
    localStorage.setItem(Authorization, value);
  },
  setToken: (value: string) => {
    localStorage.setItem(Token, value);
  },
  setUserInfo: (value: string | Record<string, unknown>) => {
    let valueStr = typeof value !== 'string' ? JSON.stringify(value) : value;
    localStorage.setItem(UserInfo, valueStr);
  },
  setItems: (pairs: Record<string, string>) => {
    Object.entries(pairs).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  },
  removeAuthorization: () => {
    localStorage.removeItem(Authorization);
  },
  removeAll: () => {
    KeySet.forEach((x) => {
      localStorage.removeItem(x);
    });
    // A page saved for after sign-in belongs to the user whose data this clears.
    sessionStorage.removeItem(LoginReturnToKey);
    removeOidcData();
  },
  setLanguage: (lng: string) => {
    localStorage.setItem('lng', lng);
  },
  getLanguage: (): string => {
    return localStorage.getItem('lng') as string;
  },
};

export const getAuthorization = () => {
  // Try OIDC token first (from Keycloak)
  const oidcToken = getOidcAccessTokenSync();
  if (oidcToken) {
    return `Bearer ${oidcToken}`;
  }

  // Fallback to legacy auth
  const auth = getSearchValue('auth');
  const authorization = auth
    ? 'Bearer ' + auth
    : storage.getAuthorization() || '';

  return authorization;
};

export default storage;

// The page that sent the user to sign in, so sign-in can land them back on it (a
// pasted share link, an expired session). sessionStorage, because it belongs to the
// tab and survives the round trip to the identity provider.

/** The page to come back to, without the legacy `auth` token parameter (getAuthorization):
 *  a token must not be stored and replayed. */
export function returnToPath(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.delete('auth');
  const rest = params.toString();
  return pathname + (rest ? `?${rest}` : '');
}

// Will not jump to the login page
/** `remember: false` on logout: the page belongs to the user signing out, and the next one to
 * sign in on this tab must not be sent back to it. */
export function redirectToLogin({
  remember = true,
}: { remember?: boolean } = {}) {
  const here = returnToPath(location.pathname, location.search);
  if (!remember) {
    sessionStorage.removeItem(LoginReturnToKey);
  } else if (!here.startsWith('/login-next')) {
    sessionStorage.setItem(LoginReturnToKey, here);
  }
  window.location.href = location.origin + `/login-next`;
}

/** Where to go after signing in: the saved page, used once, or '/' as before. */
export function takeLoginReturnTo(): string {
  const saved = sessionStorage.getItem(LoginReturnToKey);
  sessionStorage.removeItem(LoginReturnToKey);
  // Only a path inside this app: '//host' and '/\host' are another origin to a browser,
  // which drops tabs and newlines first, so '/\t/host' is '//host' too.
  const path = (saved ?? '').replace(/[\t\n\r]/g, '');
  const isLocalPath =
    path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');
  return isLocalPath ? path : '/';
}
